import React from 'react';
import { Alert, Box, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FinanceGroupSchemeDto } from '../../../../common/interfaces/invoicing.interface';
import { BillingItemTypeEnum } from '../../../../common/enums/invoicing.enum';
import { FINANCE_CONTRACTS } from '../../../../common/constants/routes.constants';
import { formatMoney } from '../../../../utilities/helper/money.helper';
import { cardSx } from './DebtDonut';

/** "/ mesec" for what is billed once a month, "/ dan" for what is billed by attendance. */
const unitKey = (type: string) =>
  type === BillingItemTypeEnum.ATTENDANCE_BASED ? 'finance.groupScheme.perDay' : 'finance.groupScheme.perMonth';

/**
 * What a group is billed by: the price list, the groups that share it, what it charges, and
 * how many children pay something else. A group without one is the warning, since its
 * children without a price of their own get no invoice at all.
 */
const GroupSchemeCard: React.FC<{
  scheme: FinanceGroupSchemeDto | null;
  kindergartenId: string;
  groupId: string;
  onEdit: () => void;
}> = ({ scheme, kindergartenId, groupId, onEdit }) => {
  const { t } = useTranslation();
  const contracts = (
    <Link
      component={RouterLink}
      to={`${FINANCE_CONTRACTS}?kindergartenId=${kindergartenId}&group=${groupId}`}
      underline="hover"
      sx={{ fontSize: 14, fontWeight: 500 }}
    >
      {t('finance.groupScheme.contracts')} →
    </Link>
  );
  return (
    <Box component="section" aria-labelledby="finance-group-scheme" sx={cardSx}>
      <Typography id="finance-group-scheme" component="h2" sx={{ fontSize: 16, fontWeight: 600, mb: 1.5 }}>
        {t('finance.groupScheme.title')}
      </Typography>
      {scheme ? (
        <>
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
            spacing={2}
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 1.5 }}
          >
            <Typography sx={{ fontSize: 20, fontWeight: 600 }}>{scheme.name}</Typography>
            {!!scheme.sharedWithGroups.length && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                {t('finance.groupScheme.shared', { groups: scheme.sharedWithGroups.join(', ') })}
              </Typography>
            )}
          </Stack>
          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mb: 1.5 }}>
            {scheme.items.map((item, index) => (
              <Box
                component="li"
                key={`${item.name}-${index}`}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                  py: 0.75,
                  borderBottom: 1,
                  borderColor: 'line',
                  fontSize: 14
                }}
              >
                <span>{item.name}</span>
                <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {formatMoney(item.unitPrice)} {t(unitKey(item.type))}
                </Box>
              </Box>
            ))}
          </Box>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>
            {t('finance.groupScheme.ownPrice', { count: scheme.childrenWithOwnPrice, total: scheme.childrenTotal })}
          </Typography>
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            <Link component="button" type="button" onClick={onEdit} underline="hover" sx={{ fontSize: 14, fontWeight: 500 }}>
              {t('finance.groupScheme.edit')} →
            </Link>
            {contracts}
          </Stack>
        </>
      ) : (
        <>
          <Alert severity="warning" sx={{ borderRadius: 2, mb: 1.5 }}>
            {t('finance.groupScheme.none')}
          </Alert>
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            <Link component="button" type="button" onClick={onEdit} underline="hover" sx={{ fontSize: 14, fontWeight: 500 }}>
              {t('finance.groupScheme.add')} →
            </Link>
            {contracts}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default GroupSchemeCard;
